/**
 * Bank colors mapping
 * 
 * Maps known bank names to their official brand colors.
 * Used to display personalized account icons.
 */

export interface BankColorConfig {
  /** Primary color (used for background gradient start) */
  primary: string;
  /** Secondary color (used for background gradient end) */
  secondary: string;
  /** Text/icon color */
  text: string;
  /** Initials to display (max 3 characters) */
  initials: string;
  /** Optional logo image path (relative to /banks/) */
  logo?: string;
}

/**
 * Known banks with their official colors
 * 
 * Colors are sourced from official bank branding guidelines.
 * Format: bank name keywords (lowercase) -> color config
 */
const BANK_COLORS: Record<string, BankColorConfig> = {
  // French Banks
  'bnp': {
    primary: '#00915A',
    secondary: '#006B43',
    text: '#FFFFFF',
    initials: 'BNP',
    logo: 'bnp.png',
  },
  'bnp paribas': {
    primary: '#00915A',
    secondary: '#006B43',
    text: '#FFFFFF',
    initials: 'BNP',
    logo: 'bnp.png',
  },
  'société générale': {
    primary: '#E30613',
    secondary: '#B8050F',
    text: '#FFFFFF',
    initials: 'SG',
    logo: 'sg.png',
  },
  'societe generale': {
    primary: '#E30613',
    secondary: '#B8050F',
    text: '#FFFFFF',
    initials: 'SG',
    logo: 'sg.png',
  },
  'crédit agricole': {
    primary: '#00A651',
    secondary: '#008542',
    text: '#FFFFFF',
    initials: 'CA',
    logo: 'ca.png',
  },
  'credit agricole': {
    primary: '#00A651',
    secondary: '#008542',
    text: '#FFFFFF',
    initials: 'CA',
    logo: 'ca.png',
  },
  'la banque postale': {
    primary: '#003399',
    secondary: '#002266',
    text: '#FFD700',
    initials: 'LBP',
    logo: 'poste.png',
  },
  'banque postale': {
    primary: '#003399',
    secondary: '#002266',
    text: '#FFD700',
    initials: 'LBP',
    logo: 'poste.png',
  },
  'caisse d\'épargne': {
    primary: '#E4002B',
    secondary: '#B80023',
    text: '#FFFFFF',
    initials: 'CE',
    logo: 'ce.png',
  },
  'caisse d\'epargne': {
    primary: '#E4002B',
    secondary: '#B80023',
    text: '#FFFFFF',
    initials: 'CE',
    logo: 'ce.png',
  },
  'crédit mutuel': {
    primary: '#0055A4',
    secondary: '#004080',
    text: '#FFFFFF',
    initials: 'CM',
    logo: 'cm.png',
  },
  'credit mutuel': {
    primary: '#0055A4',
    secondary: '#004080',
    text: '#FFFFFF',
    initials: 'CM',
    logo: 'cm.png',
  },
  'lcl': {
    primary: '#0046AD',
    secondary: '#003580',
    text: '#FFD100',
    initials: 'LCL',
    logo: 'lcl.png',
  },
  'hsbc': {
    primary: '#DB0011',
    secondary: '#A8000D',
    text: '#FFFFFF',
    initials: 'HSBC',
    logo: 'hsbc.png',
  },
  'indy': {
    primary: '#6366F1',
    secondary: '#4F46E5',
    text: '#FFFFFF',
    initials: 'IND',
    logo: 'indy.png',
  },
  'boursorama': {
    primary: '#FF6600',
    secondary: '#CC5200',
    text: '#FFFFFF',
    initials: 'BRS',
    logo: 'bourso.png',
  },
  'boursobank': {
    primary: '#FF6600',
    secondary: '#CC5200',
    text: '#FFFFFF',
    initials: 'BRS',
    logo: 'bourso.png',
  },
  'fortuneo': {
    primary: '#8B0000',
    secondary: '#5C0000',
    text: '#FFFFFF',
    initials: 'FTN',
    logo: 'fortuneo.png',
  },
  'ing': {
    primary: '#FF6600',
    secondary: '#CC5200',
    text: '#FFFFFF',
    initials: 'ING',
    logo: 'ing.png',
  },
  'hello bank': {
    primary: '#00D2A0',
    secondary: '#00A07A',
    text: '#FFFFFF',
    initials: 'HB',
    logo: 'hello.png',
  },
  'hellobank': {
    primary: '#00D2A0',
    secondary: '#00A07A',
    text: '#FFFFFF',
    initials: 'HB',
    logo: 'hello.png',
  },
  'n26': {
    primary: '#36A18B',
    secondary: '#2D8573',
    text: '#FFFFFF',
    initials: 'N26',
    logo: 'n26.png',
  },
  'qonto': {
    primary: '#FF6B00',
    secondary: '#E55A00',
    text: '#FFFFFF',
    initials: 'QTO',
    logo: 'qonto.png',
  },
  'nickel': {
    primary: '#FF5722',
    secondary: '#E64A19',
    text: '#FFFFFF',
    initials: 'NCK',
    logo: 'nickel.png',
  },
  'milleis': {
    primary: '#1E3A5F',
    secondary: '#152B47',
    text: '#FFFFFF',
    initials: 'MLS',
    logo: 'melleis.png',
  },
  'monabanq': {
    primary: '#6B2D5B',
    secondary: '#4F2143',
    text: '#FFFFFF',
    initials: 'MBQ',
    logo: 'mona.png',
  },
  'crédit coopératif': {
    primary: '#00A859',
    secondary: '#008847',
    text: '#FFFFFF',
    initials: 'CC',
    logo: 'cooperatif.png',
  },
  'credit cooperatif': {
    primary: '#00A859',
    secondary: '#008847',
    text: '#FFFFFF',
    initials: 'CC',
    logo: 'cooperatif.png',
  },
  'revolut': {
    primary: '#191C1F',
    secondary: '#000000',
    text: '#FFFFFF',
    initials: 'REV',
    logo: 'revolut.png',
  },
  'orange bank': {
    primary: '#FF6600',
    secondary: '#CC5200',
    text: '#000000',
    initials: 'OB',
  },
  'axa banque': {
    primary: '#00008F',
    secondary: '#000066',
    text: '#FFFFFF',
    initials: 'AXA',
  },
  'banque populaire': {
    primary: '#0055A4',
    secondary: '#004080',
    text: '#FFFFFF',
    initials: 'BP',
    logo: 'bp.png',
  },
  'cic': {
    primary: '#E4002B',
    secondary: '#B80023',
    text: '#FFFFFF',
    initials: 'CIC',
  },
  
  // Belgian Banks
  'belfius': {
    primary: '#8C1D82',
    secondary: '#6B1663',
    text: '#FFFFFF',
    initials: 'BEL',
  },
  'kbc': {
    primary: '#003B5C',
    secondary: '#002940',
    text: '#FFFFFF',
    initials: 'KBC',
  },
  'argenta': {
    primary: '#00A651',
    secondary: '#008542',
    text: '#FFFFFF',
    initials: 'ARG',
  },
  
  // International Banks
  'deutsche bank': {
    primary: '#0018A8',
    secondary: '#001280',
    text: '#FFFFFF',
    initials: 'DB',
  },
  'barclays': {
    primary: '#00AEEF',
    secondary: '#008BC0',
    text: '#FFFFFF',
    initials: 'BAR',
  },
  'santander': {
    primary: '#EC0000',
    secondary: '#B80000',
    text: '#FFFFFF',
    initials: 'SAN',
  },
  'ubs': {
    primary: '#E60000',
    secondary: '#B80000',
    text: '#FFFFFF',
    initials: 'UBS',
  },
  'credit suisse': {
    primary: '#003366',
    secondary: '#00264D',
    text: '#FFFFFF',
    initials: 'CS',
  },
  
  // Compte générique
  'compte joint': {
    primary: '#8B5CF6',
    secondary: '#6D28D9',
    text: '#FFFFFF',
    initials: 'CJ',
  },
  'compte courant': {
    primary: '#6366F1',
    secondary: '#4F46E5',
    text: '#FFFFFF',
    initials: 'CC',
  },
  'livret a': {
    primary: '#10B981',
    secondary: '#059669',
    text: '#FFFFFF',
    initials: 'LA',
  },
  'livret': {
    primary: '#10B981',
    secondary: '#059669',
    text: '#FFFFFF',
    initials: 'LIV',
  },
  'pel': {
    primary: '#F59E0B',
    secondary: '#D97706',
    text: '#FFFFFF',
    initials: 'PEL',
  },
  'pea': {
    primary: '#3B82F6',
    secondary: '#2563EB',
    text: '#FFFFFF',
    initials: 'PEA',
  },
};

/**
 * Default colors for unknown banks
 */
const DEFAULT_COLORS: BankColorConfig = {
  primary: '#8B5CF6', // violet-500
  secondary: '#6366F1', // indigo-500
  text: '#FFFFFF',
  initials: '',
  logo: 'budgira.png',
};

/**
 * Extracts initials from an account name
 * Takes the first letter of each word, up to 3 characters
 */
function extractInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    // Single word: take first 2-3 characters
    return name.substring(0, 3).toUpperCase();
  }
  // Multiple words: take first letter of each word
  return words
    .slice(0, 3)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

/**
 * Gets the color configuration for a bank account
 * 
 * @param accountName - The name of the bank account
 * @returns Color configuration for the account icon
 */
export function getBankColors(accountName: string): BankColorConfig {
  const normalizedName = accountName.toLowerCase().trim();
  
  // Try exact match first
  if (BANK_COLORS[normalizedName]) {
    return BANK_COLORS[normalizedName];
  }
  
  // Try partial match (check if account name contains a known bank name)
  for (const [bankKey, colors] of Object.entries(BANK_COLORS)) {
    if (normalizedName.includes(bankKey) || bankKey.includes(normalizedName)) {
      return colors;
    }
  }
  
  // Return default colors with extracted initials
  return {
    ...DEFAULT_COLORS,
    initials: extractInitials(accountName),
  };
}

/**
 * List of all supported banks (for documentation/autocomplete)
 */
export const SUPPORTED_BANKS = Object.keys(BANK_COLORS).filter(
  (key, index, self) => 
    // Remove duplicates (same bank with different spellings)
    self.findIndex(k => BANK_COLORS[k]?.initials === BANK_COLORS[key]?.initials) === index
);
